import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Palette, Gift, PenLine, ArrowLeft, Heart, Star, Wand2, Camera,
  MessageSquare, Crown, Flower2,
} from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgOf = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);

export const STUDIO_CARD_ICONS = {
  Palette, Gift, PenLine, Sparkles, Heart, Star, Wand2, Camera, MessageSquare, Crown, Flower2,
};

// احتياط فقط إن تعذّر الوصول للـAPI — نفس البطاقات المهيأة بالباكند
const FALLBACK = [
  { icon: "Palette",
    image: "https://static.prod-images.emergentagent.com/jobs/757865c7-80d0-4582-ad8c-41c9eb28bb58/images/6bfa8ef7b38236d6e0b4955af91a518163364ae036dd18058a23852fbd36d631.jpeg",
    title_ar: "غيّر لون الورد", title_en: "Recolor the flowers",
    desc_ar: "نفس باقتنا «ورد فاخر مشكّل» — قبل وبعد تغيير اللون للأحمر. اختر من ١٧ لوناً وشاهد النتيجة خلال ثوانٍ.",
    desc_en: "Our actual 'Mixed Luxury Roses' — before & after recoloring to red. Pick from 17 shades and see it in seconds." },
  { icon: "Gift",
    image: "https://static.prod-images.emergentagent.com/jobs/757865c7-80d0-4582-ad8c-41c9eb28bb58/images/2ea55f6a7c67cc1b622586258cfccfa617d557aa095035e38ad01e5f888e970c.jpeg",
    title_ar: "بدّل لون التغليف", title_en: "Restyle the wrapping",
    desc_ar: "نفس الورود بتغليفين مختلفين: أخضر هادئ أو أسود فاخر بشريط ذهبي — نسّقه بلونك المفضل.",
    desc_en: "The same roses in two wraps: calm sage or luxurious black with gold ribbon — match it to your taste." },
  { icon: "PenLine",
    image: "https://static.prod-images.emergentagent.com/jobs/757865c7-80d0-4582-ad8c-41c9eb28bb58/images/5a463274beeb0ddf42c5d427c26857fa9a916258f3496deb71a14b167a84ce00.jpeg",
    title_ar: "اكتب رسالتك على البوكس", title_en: "Write your message on the box",
    desc_ar: "نفس الباقة في بوكس أسود كُتب عليه «أحبك يا أمي» بحبر ذهبي — اكتب أي عبارة من قلبك.",
    desc_en: "The same bouquet in a black box inscribed in gold ink — write any words from your heart." },
];

const FOCUS_CLASS = { top: "object-top", bottom: "object-bottom", center: "object-center" };

/* قسم تسويقي يبرز مميزات ستوديو الذكاء — البطاقات تُدار بالكامل من لوحة الأدمن
   (تبويب «إدارة استديو الذكاء») وتصل من الـAPI بلا أي تحديث للتطبيق */
export default function AppFeatures() {
  const { lang } = useApp();
  const ar = lang === "ar";
  const [cards, setCards] = useState(null);
  const sectionRef = useRef(null);
  const seenRef = useRef(false);

  useEffect(() => {
    http.get("/studio-cards")
      .then(({ data }) => { if (data?.cards?.length) setCards(data.cards); })
      .catch(() => {});
  }, []);

  // إحصائيات: مشاهدة واحدة عند ظهور القسم فعلياً + نقرات البطاقات
  useEffect(() => {
    if (!cards || !sectionRef.current || seenRef.current) return;
    const ob = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenRef.current) {
        seenRef.current = true;
        http.post("/studio-cards/track", { event: "impression", card_ids: cards.map((c) => c.id) }).catch(() => {});
        ob.disconnect();
      }
    }, { threshold: 0.3 });
    ob.observe(sectionRef.current);
    return () => ob.disconnect();
  }, [cards]);

  const trackClick = (c) => {
    if (c.id) http.post("/studio-cards/track", { event: "click", card_id: c.id }).catch(() => {});
  };

  const list = cards || FALLBACK;

  return (
    <section ref={sectionRef} className="rounded-[1.5rem] overflow-hidden p-4 sm:p-6 text-white"
             style={{ background: "linear-gradient(135deg, #0b2f22, #105438 60%, #14603f)" }}
             data-testid="home-app-features">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/90 font-bold">
        <Sparkles size={13} /> {ar ? "حصرياً في RG" : "Only at RG"}
      </div>
      <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-xl sm:text-2xl mt-1.5 leading-snug`}>
        {ar ? "صمّم هديتك" : "Design your gift"}
      </h2>
      <p className="text-[12.5px] text-white/75 mt-1">
        {ar ? "بالذكاء الاصطناعي — شاهد باقتك بالشكل النهائي قبل أن تطلبها" : "AI-powered — see the final look before you order"}
      </p>

      <div className="mt-4 flex gap-3 overflow-x-auto scroll-x-hide snap-x -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
        {list.map((c, i) => {
          const Icon = STUDIO_CARD_ICONS[c.icon] || Sparkles;
          const title = ar ? c.title_ar : c.title_en || c.title_ar;
          const desc = ar ? c.desc_ar : c.desc_en || c.desc_ar;
          const img = imgOf(c.image);
          const imgM = imgOf(c.image_mobile);
          const pos = FOCUS_CLASS[c.focus] || "object-center";
          const to = c.link || "/design-studio";
          const external = /^https?:/i.test(to);
          const inner = (
            <>
              <div className="relative">
                <picture>
                  {imgM && <source media="(max-width: 640px)" srcSet={imgM} />}
                  <img src={img} alt={title} loading="lazy" decoding="async"
                       className={`w-full aspect-[4/3] object-cover ${pos}`} />
                </picture>
                <span className="absolute top-2.5 start-2.5 w-8 h-8 rounded-full flex items-center justify-center text-white shadow"
                      style={{ background: "rgba(13,59,42,0.85)", backdropFilter: "blur(4px)" }}>
                  <Icon size={15} />
                </span>
              </div>
              <div className="p-3.5">
                <div className="font-bold text-[14px]">{title}</div>
                <div className="text-[12px] text-white/70 mt-1 leading-relaxed">{desc}</div>
              </div>
            </>
          );
          const cls = "shrink-0 snap-start w-[76%] sm:w-[300px] rounded-2xl overflow-hidden bg-white/[0.07] border border-white/10 backdrop-blur-sm block active:scale-[0.98] transition-transform";
          return external ? (
            <a key={c.id || i} href={to} target="_blank" rel="noreferrer" className={cls}
               onClick={() => trackClick(c)}
               data-testid={`app-feature-${i + 1}`}>{inner}</a>
          ) : (
            <Link key={c.id || i} to={to} className={cls}
                  onClick={() => trackClick(c)}
                  data-testid={`app-feature-${i + 1}`}>{inner}</Link>
          );
        })}
      </div>

      <Link to="/design-studio"
            className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 text-sm font-extrabold text-emerald-950 active:scale-95 transition-transform"
            style={{ background: "linear-gradient(90deg, #e9c97a, #d4af37)", boxShadow: "0 8px 22px -8px rgba(212,175,55,0.55)" }}
            data-testid="app-features-cta">
      {ar ? "اكتشف الاستوديو وجرّبه الآن — مجاناً" : "Discover the studio & try it now — free"}
        <ArrowLeft size={15} className={ar ? "" : "rotate-180"} />
      </Link>
    </section>
  );
}
